import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface Agent {
  id: string;
  user_id: string;
  agent_id: string;
  full_name: string;
  phone: string | null;
  role: 'admin' | 'makler' | 'call_center';
  status: 'active' | 'inactive';
  /** Synthetic Better-Auth identifier (`<agent_id>@4m-immo.local`). Used
   *  for the auth layer; not a human-facing address. */
  email: string;
  /** Real contact email stored on profiles.email — what admin sets in the
   *  agent dialog and what marketing / SMTP fan-out targets. Nullable until
   *  operator fills it in. */
  contact_email: string | null;
  avatar_url?: string | null;
  morning_briefing_enabled: number;
  cc_default_makler_id?: string | null;
  /** Batch AB — per-user Zinshausliste access (0 / 1). */
  zinshaus_access?: number;
  /** Seller rules (0055) — real seller-phone visibility (0 / 1 from raw SQL). */
  phone_access?: number;
  /** Seller rules (0055) — compliance-manager flag (0 / 1 from raw SQL). */
  seller_compliance?: number;
  /** True for bootstrap admins from ADMIN_PASSWORDS_JSON (admin1/admin2/…).
   *  Those are managed via the secret and are NOT deactivatable/resettable
   *  through the UI. Profile-based admins (created by admin1) are false. */
  is_secret_admin?: boolean;
  created_at: number;
  clients_count: number;
  properties_count: number;
  appointments_count: number;
  last_activity_at: number | null;
}

export interface AgentSnapshot {
  total: number;
  active: number;
  makler: number;
  call_center: number;
  admin: number;
}

export function useAgents() {
  return useQuery({
    queryKey: ['agents'] as const,
    queryFn: async () => {
      const res = await apiFetch('/agents');
      if (!res.ok) throw new Error('agents_failed');
      const json = (await res.json()) as { items: Agent[] };
      return json.items;
    },
    staleTime: 30_000,
  });
}

export function useAgentSnapshot() {
  return useQuery({
    queryKey: ['agents', 'snapshot'] as const,
    queryFn: async () => {
      const res = await apiFetch('/agents/snapshot');
      if (!res.ok) throw new Error('agent_snapshot_failed');
      return (await res.json()) as AgentSnapshot;
    },
    staleTime: 60_000,
  });
}

export interface CreateAgentInput {
  full_name: string;
  phone?: string | null;
  /** Contact email stored on profiles.email. Optional but recommended. */
  email?: string | null;
  /** 'admin' is only accepted by the backend when the caller is admin1. */
  role: 'makler' | 'call_center' | 'admin';
  agent_id?: string;
  password?: string;
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAgentInput) => {
      const res = await apiFetch('/agents', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'create_failed');
      }
      return (await res.json()) as {
        ok: true;
        profile_id: string;
        agent_id: string;
        initial_password: string;
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });
}

export interface UpdateAgentInput {
  full_name?: string;
  phone?: string | null;
  /** Contact email — written to profiles.email. Empty string clears it. */
  email?: string | null;
  role?: 'makler' | 'call_center';
  status?: 'active' | 'inactive';
  morning_briefing_enabled?: boolean;
  cc_default_makler_id?: string | null;
  /** Batch AB — per-user Zinshausliste page access. */
  zinshaus_access?: boolean;
  /** Seller rules (0055) — only rendered/editable in the super-admin UI. */
  phone_access?: boolean;
  seller_compliance?: boolean;
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateAgentInput & { id: string }) => {
      const res = await apiFetch(`/agents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'update_failed');
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });
}

export function useDeactivateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/agents/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'delete_failed');
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });
}

/**
 * Permanently deletes an INACTIVE agent's profile + Better-Auth user row.
 * Backend rejects with 409 if the agent is still active. Cascades chat data,
 * push subscriptions, email verifications. Sets NULL on the agent's clients,
 * appointments, SMS log, automation rules — those records stay but become
 * unassigned.
 */
export function useHardDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/agents/${id}/hard`, { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `hard_delete_failed_${res.status}`);
      }
      return (await res.json()) as { ok: true; hard_deleted: true };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });
}

export function useResetAgentPassword() {
  return useMutation({
    mutationFn: async (input: { id: string; newPassword?: string }) => {
      const res = await apiFetch(`/agents/${input.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify(input.newPassword ? { new_password: input.newPassword } : {}),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'reset_failed');
      }
      return (await res.json()) as { ok: true; new_password: string };
    },
  });
}
